import React from 'react';
import { Box, Typography, Stack, Breadcrumbs, Link, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import TableCount from '../TableCount';

export default function PageHeader({
  title,
  subtitle,
  count,
  countLabel = 'items',
  action,
  secondaryAction,
  breadcrumbs,
  actionLabel,
  actionIcon,
  onAction,
  sx = {},
}) {
  const primaryAction = action || (actionLabel ? (
    <Button
      variant="contained"
      startIcon={actionIcon}
      onClick={onAction}
      sx={{
        fontWeight: 700,
        borderRadius: 2,
        px: 2.25,
        py: 0.9,
        textTransform: 'none',
        fontSize: '0.875rem',
      }}
    >
      {actionLabel}
    </Button>
  ) : null);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: { xs: 2.5, md: 3.5 },
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              mb: 0.75,
              fontSize: '0.8125rem',
              '& .MuiBreadcrumbs-separator': { mx: 0.75, color: 'text.disabled' },
            }}
          >
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return isLast || !crumb.path ? (
                <Typography
                  key={crumb.label}
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.label}
                  component={RouterLink}
                  to={crumb.path}
                  underline="hover"
                  color="inherit"
                  sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.35rem', sm: '1.65rem', md: '1.85rem' },
            }}
          >
            {title}
          </Typography>
          {typeof count === 'number' && (
            <TableCount total={count} label={countLabel} />
          )}
        </Stack>

        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', md: '0.875rem' },
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {(primaryAction || secondaryAction) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flexShrink: 0 }}
        >
          {secondaryAction}
          {primaryAction}
        </Stack>
      )}
    </Box>
  );
}
